import React from "react";
import styles from "../../../pages/Reporting/Reporting.module.css";

type ReportFiltersState = {
    [key: string]: {
        search?: string;
        geozone?: string;
        geoBuildMode?: "union" | "single";
        geoAddress?: string;
        locality?: string;
        dateFrom?: string;
        dateTo?: string;
        personType?: "all" | "juridical" | "ip" | "physical";
        contractor?: string;
        car?: string;
    };
};

type Props = {
    activeSheet: string;
    currentFilters: ReportFiltersState[string];
    sheetCache: Record<string, any[]>;
    onSearchChange: (value: string) => void;
    onGeozoneChange: (value: string) => void;
    onGeoBuildModeChange: (value: "union" | "single") => void;
    onGeoAddressChange: (value: string) => void;
    onLocalityChange: (value: string) => void;
    onDateFromChange: (value: string) => void;
    onDateToChange: (value: string) => void;
    onPersonTypeChange: (
        value: "all" | "juridical" | "ip" | "physical"
    ) => void;
    onContractorChange: (value: string) => void;
    onCarChange: (value: string) => void;
};

const ReportFilters: React.FC<Props> = ({
    activeSheet,
    currentFilters,
    sheetCache,
    onSearchChange,
    onGeozoneChange,
    onGeoBuildModeChange,
    onGeoAddressChange,
    onLocalityChange,
    onDateFromChange,
    onDateToChange,
    onPersonTypeChange,
    onContractorChange,
    onCarChange,
}) => {
    return (
        <div className={styles.filtersBar}>
            <div className={styles.filterGroup}>
                <div className={styles.filterField}>
                    <span className={styles.filterLabel}>Общий поиск</span>
                    <input
                        className={styles.filterSelect}
                        placeholder="Поиск по всем полям..."
                        value={currentFilters?.search ?? ""}
                        onChange={e => onSearchChange(e.target.value)}
                    />
                </div>

                {activeSheet === "list" && (
                    <div className={styles.filterField}>
                        <span className={styles.filterLabel}>Геозона</span>
                        <select
                            className={styles.filterSelect}
                            value={currentFilters?.geozone ?? "all"}
                            onChange={e => onGeozoneChange(e.target.value)}
                        >
                            <option value="all">Все геозоны</option>
                            {}
                        </select>
                    </div>
                )}

                {activeSheet === "car" && (
                    <>
                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Подрядчик / организация
                            </span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.contractor ?? "all"}
                                onChange={e =>
                                    onContractorChange(e.target.value)
                                }
                            >
                                <option value="all">Все подрядчики</option>
                                {/* Заполним из данных позже */}
                            </select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Автомобиль
                            </span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.car ?? "all"}
                                onChange={e => onCarChange(e.target.value)}
                            >
                                <option value="all">Все автомобили</option>
                                {/* Список автомобилей заполним позже */}
                            </select>
                        </div>
                    </>
                )}

                {activeSheet === "geo" && (
                    <>
                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>Геозона</span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.geozone ?? "all"}
                                onChange={e => onGeozoneChange(e.target.value)}
                            >
                                <option value="all">Все геозоны</option>
                                {Array.from(
                                    new Set(
                                        sheetCache.geo?.map(
                                            r => (r as any).geoZone
                                        )
                                    )
                                ).map(zone => (
                                    <option key={zone as string} value={zone as string}>
                                        {zone as string}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Построение геозоны
                            </span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.geoBuildMode ?? "union"}
                                onChange={e =>
                                    onGeoBuildModeChange(
                                        e.target.value as "union" | "single"
                                    )
                                }
                            >
                                <option value="union">
                                    Объединение адресов и КП
                                </option>
                                <option value="single">Конкретный адрес</option>
                            </select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Конкретный адрес
                            </span>
                            <input
                                className={styles.filterSelect}
                                placeholder="Введите адрес"
                                value={currentFilters?.geoAddress ?? ""}
                                onChange={e =>
                                    onGeoAddressChange(e.target.value)
                                }
                            />
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Даты отображения маршрутов
                            </span>
                            <div style={{ display: "flex", gap: 8 }}>
                                <input
                                    type="date"
                                    className={styles.filterSelect}
                                    value={currentFilters?.dateFrom ?? ""}
                                    onChange={e =>
                                        onDateFromChange(e.target.value)
                                    }
                                />
                                <input
                                    type="date"
                                    className={styles.filterSelect}
                                    value={currentFilters?.dateTo ?? ""}
                                    onChange={e =>
                                        onDateToChange(e.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>Лицо</span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.personType ?? "all"}
                                onChange={e =>
                                    onPersonTypeChange(
                                        e.target.value as
                                            | "all"
                                            | "juridical"
                                            | "ip"
                                            | "physical"
                                    )
                                }
                            >
                                <option value="all">Все</option>
                                <option value="juridical">
                                    Юридическое лицо
                                </option>
                                <option value="ip">
                                    Индивидуальный предприниматель
                                </option>
                                <option value="physical">
                                    Физическое лицо
                                </option>
                            </select>
                        </div>
                    </>
                )}

                {activeSheet === "frameCar" && (
                    <>
                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Подрядчик / организация
                            </span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.contractor ?? "all"}
                                onChange={e =>
                                    onContractorChange(e.target.value)
                                }
                            >
                                <option value="all">Все подрядчики</option>
                                {}
                            </select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Автомобиль
                            </span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.car ?? "all"}
                                onChange={e => onCarChange(e.target.value)}
                            >
                                <option value="all">Все автомобили</option>
                                {}
                            </select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>Геозона</span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.geozone ?? "all"}
                                onChange={e => onGeozoneChange(e.target.value)}
                            >
                                <option value="all">Все геозоны</option>
                                {}
                            </select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Даты отображения маршрутов
                            </span>
                            <div style={{ display: "flex", gap: 8 }}>
                                <input
                                    type="date"
                                    className={styles.filterSelect}
                                    value={currentFilters?.dateFrom ?? ""}
                                    onChange={e =>
                                        onDateFromChange(e.target.value)
                                    }
                                />
                                <input
                                    type="date"
                                    className={styles.filterSelect}
                                    value={currentFilters?.dateTo ?? ""}
                                    onChange={e =>
                                        onDateToChange(e.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>Лицо</span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.personType ?? "all"}
                                onChange={e =>
                                    onPersonTypeChange(
                                        e.target.value as
                                            | "all"
                                            | "juridical"
                                            | "ip"
                                            | "physical"
                                    )
                                }
                            >
                                <option value="all">Все</option>
                                <option value="juridical">
                                    Юридическое лицо
                                </option>
                                <option value="ip">
                                    Индивидуальный предприниматель
                                </option>
                                <option value="physical">
                                    Физическое лицо
                                </option>
                            </select>
                        </div>
                    </>
                )}

                {activeSheet === "failure" && (
                    <>
                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Построение геозоны
                            </span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.geoBuildMode ?? "union"}
                                onChange={e =>
                                    onGeoBuildModeChange(
                                        e.target.value as "union" | "single"
                                    )
                                }
                            >
                                <option value="union">
                                    Объединение адресов и КП
                                </option>
                                <option value="single">Конкретный адрес</option>
                            </select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Конкретный адрес
                            </span>
                            <input
                                className={styles.filterSelect}
                                placeholder="Введите адрес"
                                value={currentFilters?.geoAddress ?? ""}
                                onChange={e =>
                                    onGeoAddressChange(e.target.value)
                                }
                            />
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Населенные пункты
                            </span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.locality ?? "all"}
                                onChange={e =>
                                    onLocalityChange(e.target.value)
                                }
                            >
                                <option value="all">
                                    Все населенные пункты
                                </option>
                                {/* Конкретные НП подставим позже */}
                            </select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Даты отображения маршрутов
                            </span>
                            <div style={{ display: "flex", gap: 8 }}>
                                <input
                                    type="date"
                                    className={styles.filterSelect}
                                    value={currentFilters?.dateFrom ?? ""}
                                    onChange={e =>
                                        onDateFromChange(e.target.value)
                                    }
                                />
                                <input
                                    type="date"
                                    className={styles.filterSelect}
                                    value={currentFilters?.dateTo ?? ""}
                                    onChange={e =>
                                        onDateToChange(e.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>Лицо</span>
                            <select
                                className={styles.filterSelect}
                                value={currentFilters?.personType ?? "all"}
                                onChange={e =>
                                    onPersonTypeChange(
                                        e.target.value as
                                            | "all"
                                            | "juridical"
                                            | "ip"
                                            | "physical"
                                    )
                                }
                            >
                                <option value="all">Все</option>
                                <option value="juridical">
                                    Юридическое лицо
                                </option>
                                <option value="ip">
                                    Индивидуальный предприниматель
                                </option>
                                <option value="physical">
                                    Физическое лицо
                                </option>
                            </select>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ReportFilters;


